import type { HeaderV2Link, MegaMenuData } from "../sections/HeaderV2/types";
import type { DynamicContents, DynamicContentsData } from "./cms";
import { clampMegaMenuMaxItems, MegaMenuStatus, resolveMegaMenuSource, type MegaMenuSource } from "./megaMenuSource";

const HEADER_V2_NODE_TYPE = "HeaderV2Wrapper";

type CraftNode = {
  type?: string | { resolvedName?: string };
  props?: { links?: HeaderV2Link[]; megaMenu?: { maxItems?: number } };
  [key: string]: unknown;
};

export type CraftTree = Record<string, CraftNode | undefined>;

const resolvedNameOf = ({ node }: { node?: CraftNode }) =>
  typeof node?.type === "object" ? node.type?.resolvedName : node?.type;

const megaMenuSources = ({ links }: { links?: HeaderV2Link[] }): MegaMenuSource[] =>
  (links ?? []).flatMap((link) => {
    const source = resolveMegaMenuSource({ link });
    return source ? [source] : [];
  });

export const megaMenuContentKey = ({ nodeId, linkId }: { nodeId: string; linkId: string }) =>
  `${nodeId}_megamenu_${linkId}`;

export const extractMegaMenuContents = ({ tree }: { tree?: CraftTree | null }): DynamicContents => {
  const contents: DynamicContents = {};

  for (const [nodeId, node] of Object.entries(tree ?? {})) {
    if (resolvedNameOf({ node }) !== HEADER_V2_NODE_TYPE) continue;

    const max = clampMegaMenuMaxItems({ value: node?.props?.megaMenu?.maxItems });

    for (const { linkId, contentTypeId } of megaMenuSources({ links: node?.props?.links })) {
      contents[megaMenuContentKey({ nodeId, linkId })] = { contentTypeId, max, includeMetadata: true };
    }
  }

  return contents;
};

export const readMegaMenuData = ({
  nodeId,
  links,
  dynamicContentsData,
}: {
  nodeId?: string;
  links?: HeaderV2Link[];
  dynamicContentsData?: DynamicContentsData;
}): MegaMenuData => {
  const data: MegaMenuData = {};
  if (!nodeId) return data;

  for (const { linkId } of megaMenuSources({ links })) {
    const entries = dynamicContentsData?.[megaMenuContentKey({ nodeId, linkId })];
    data[linkId] = Array.isArray(entries)
      ? { status: MegaMenuStatus.READY, entries }
      : { status: MegaMenuStatus.ERROR, entries: [] };
  }

  return data;
};
